/**
 * CompanyAutocompleteInput — reusable autocomplete input for company-name
 * fields across CRM, Procurement and Contacts. Hits the unified institutional
 * lookup endpoint /api/crm/leads/lookup which searches:
 *   • Master Counterparties
 *   • Procurement Suppliers
 *   • Past CRM Leads
 *   • Onboarding Submissions
 *
 * Props:
 *   value           string                   current input value
 *   onChange        (string) => void         called on every keystroke
 *   onSelect        (suggestion) => void     called when user picks an entry
 *                                            (fields: company_name, country,
 *                                             role, currency, trade_license,
 *                                             trn, email, phone, contact_person,
 *                                             industry, source, id …)
 *   placeholder?    string
 *   testid?         string                   data-testid for the input
 *   className?      string                   extra classes on the input
 *   minChars?       number                   min chars to fire lookup (def 2)
 *   limit?          number                   max suggestions (def 8)
 *   disabled?       boolean
 */
import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Badge } from '../../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_BADGE = {
  counterparty: 'bg-emerald-100 text-emerald-800',
  supplier:     'bg-amber-100 text-amber-800',
  lead:         'bg-sky-100 text-sky-800',
  onboarding:   'bg-violet-100 text-violet-800',
};

export const CompanyAutocompleteInput = ({
  value,
  onChange,
  onSelect,
  placeholder = 'Start typing company name…',
  testid = 'company-autocomplete-input',
  className = '',
  minChars = 2,
  limit = 8,
  disabled = false,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!value || value.length < minChars) {
      setSuggestions([]); setShow(false); return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('admin_token');
        const r = await fetch(
          `${API_URL}/api/crm/leads/lookup?q=${encodeURIComponent(value)}&limit=${limit}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (r.ok) {
          const j = await r.json();
          setSuggestions(j); setShow(true);
        }
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [value, minChars, limit]);

  const pick = (s) => {
    setShow(false);
    if (onSelect) onSelect(s);
  };

  return (
    <div className="relative">
      <Search className="w-3.5 h-3.5 absolute left-3 top-3 text-slate-400 pointer-events-none" />
      {loading && (
        <Loader2 className="w-3.5 h-3.5 absolute right-3 top-3 text-slate-400 animate-spin" />
      )}
      <input
        type="text"
        data-testid={testid}
        disabled={disabled}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => suggestions.length && setShow(true)}
        onBlur={() => setTimeout(() => setShow(false), 200)}
        placeholder={placeholder}
        className={`pl-9 w-full px-4 py-2 border border-slate-300 rounded-lg
          focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent
          disabled:opacity-50 ${className}`}
      />
      {show && suggestions.length > 0 && (
        <ul
          data-testid={`${testid}-list`}
          className="absolute z-30 mt-1 w-full bg-white border border-slate-200
            rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {suggestions.map((s, i) => (
            <li
              key={`${s.source}-${s.id || i}`}
              data-testid={`${testid}-item-${i}`}
              onMouseDown={() => pick(s)}
              className="px-3 py-2.5 hover:bg-slate-50 cursor-pointer
                border-b border-slate-100 last:border-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[#0F172A]">
                  {s.label || s.company_name}
                </span>
                <Badge className={`${SOURCE_BADGE[s.source] || 'bg-slate-100 text-slate-700'} text-[9px] uppercase`}>
                  {s.source}
                </Badge>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {[s.country, s.role, s.email].filter(Boolean).join(' · ') || '—'}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CompanyAutocompleteInput;
